import type { AxiosResponse, InternalAxiosRequestConfig } from './types'
import qs from 'qs'
import { ElMessageBox } from 'element-plus'
import { TRANSFORM_REQUEST_DATA } from '@/constants'

let kicked = false

const defaultRequestInterceptors = (config: InternalAxiosRequestConfig) => {
  if (
    config.method === 'post' &&
    config.headers['Content-Type'] === 'application/x-www-form-urlencoded'
  ) {
    config.data = qs.stringify(config.data)
  } else if (
    TRANSFORM_REQUEST_DATA &&
    config.method === 'post' &&
    config.headers['Content-Type'] === 'multipart/form-data' &&
    !(config.data instanceof FormData)
  ) {
    // multipart/form-data 时使用 FormData
    const formData = new FormData()
    Object.keys(config.data).forEach((key) => {
      formData.append(key, config.data[key])
    })
    config.data = formData
  }
  if (config.method === 'get' && config.params) {
    let url = config.url as string
    url += '?'
    const keys = Object.keys(config.params)
    for (const key of keys) {
      if (config.params[key] !== void 0 && config.params[key] !== null) {
        url += `${key}=${encodeURIComponent(config.params[key])}&`
      }
    }
    url = url.substring(0, url.length - 1)
    config.params = {}
    config.url = url
  }
  return config
}

const defaultResponseInterceptors = (response: AxiosResponse) => {
  if (response?.config?.responseType === 'blob') {
    return response
  }

  if (response.status === 200) {
    const data = response.data as Record<string, any>

    // 兼容 { value: {...} } 和直接返回 IResponse 两种格式
    const inner = data.value ?? data

    // 检查业务状态码（兼容 status / Status 大小写）
    if (inner && typeof inner === 'object') {
      const bizStatus = inner['status'] ?? inner['Status']
      if (bizStatus === 'error') {
        const msg = inner.message ?? inner.Message
        const result = inner.result ?? inner.Result

        // 被踢下线：清除登录态 + 跳转
        if ((msg && msg.includes('其他设备登录')) || result === '401' || result === 401) {
          sessionStorage.clear()
          try {
            const raw = localStorage.getItem('user-store')
            if (raw) {
              const data = JSON.parse(raw)
              data.token = ''
              data.userInfo = null
              localStorage.setItem('user-store', JSON.stringify(data))
            }
          } catch { /* ignore */ }
          if (!kicked) {
            kicked = true
            ElMessageBox.alert('账号已在其他设备登录，请重新登录', '渝逗AI平台', {
              confirmButtonText: '确定',
              callback: () => { window.location.replace('/login') },
            })
          }
          return { status: 'error', message: '账号已被踢下线', result: null, totalCount: 0 }
        }

        // 后端部分接口对空结果返回 { status:'error', message:'OK' }，当作空数据
        if (!msg || msg === 'OK') {
          return { status: 'success', message: '', result: inner.result ?? null, totalCount: inner.totalCount ?? 0 }
        }
        return Promise.reject(new Error(msg))
      }
    }

    // 返回解析后的数据
    if (data.value) return data.value
    if (data && typeof data === 'object' && 'status' in data) return data
    return { status: 'success', message: '', result: null, totalCount: 0 }
  }

  // 非 200 状态码
  if (response.status === 401) {
    sessionStorage.clear()
    try {
      const raw = localStorage.getItem('user-store')
      if (raw) {
        const data = JSON.parse(raw)
        data.token = ''
        data.userInfo = null
        localStorage.setItem('user-store', JSON.stringify(data))
      }
    } catch { /* ignore */ }
    if (!kicked) {
      kicked = true
      window.location.replace('/login')
    }
    return { status: 'error', message: '登录已过期，请重新登录', result: null, totalCount: 0 }
  }
  // 其他非 200 状态码
  console.error(`请求失败: ${response.status} ${response.statusText}`, response.config?.url)
  return Promise.reject(new Error(`请求失败 (${response.status})`))
}

export { defaultResponseInterceptors, defaultRequestInterceptors }
